// Init Sentry
import { onEmbeddingAccepted } from "./consent";
import * as Sentry from "@sentry/browser";
import {BrowserTracing} from "@sentry/tracing";
import isErrorFromGoogleTranslate from "./isErrorFromGoogleTranslate";
const isNeosBackend = document.body.classList.contains('neos-backend');
const isDevelopment = location.href.indexOf('ddev.site') !== -1;

// Fleet privacy rule: only the audited public-parameter allowlist survives,
// mirroring the PHP-side scrubbers (see Settings.Sentry.yaml). Deliberately
// absent: go (raw selector), uri (unbounded URL value), utm_* (no browser
// diagnostic value), all tokens.
const KEEP_QUERY_PARAMS = ['search', 'currentPage', 'currentPage[]', 'period', 'province', 'district', 'entity', 'event-type'];
function filterUrl(url) {
	if (typeof url !== 'string') {
		return url;
	}
	const hashIndex = url.indexOf('#');
	if (hashIndex !== -1) {
		url = url.slice(0, hashIndex);
	}
	const queryIndex = url.indexOf('?');
	if (queryIndex === -1) {
		return url;
	}
	const keptPairs = url.slice(queryIndex + 1).split('&').filter((pair) => {
		const name = decodeURIComponent(pair.split('=')[0].replace(/\+/g, ' '));
		return KEEP_QUERY_PARAMS.includes(name);
	});
	return keptPairs.length ? url.slice(0, queryIndex) + '?' + keptPairs.join('&') : url.slice(0, queryIndex);
}
function scrubEventUrls(event) {
	if (event.request && event.request.url) {
		event.request.url = filterUrl(event.request.url);
	}
	if (event.request && event.request.headers && event.request.headers.Referer) {
		event.request.headers.Referer = filterUrl(event.request.headers.Referer);
	}
	if (event.transaction) {
		event.transaction = filterUrl(event.transaction);
	}
	(event.breadcrumbs || []).forEach((breadcrumb) => {
		if (breadcrumb.data && breadcrumb.data.url) {
			breadcrumb.data.url = filterUrl(breadcrumb.data.url);
		}
	});
	(event.spans || []).forEach((span) => {
		if (span.description) {
			span.description = filterUrl(span.description);
		}
	});
	return event;
}
if(!isNeosBackend && !isDevelopment) {
	onEmbeddingAccepted('Sentry', () => {
		Sentry.init({
			// EU project (neos-website-frontend); rollup replaces process.env.SENTRY_BROWSER_DSN at build time
			dsn: process.env.SENTRY_BROWSER_DSN || "https://193d2ca3d86a0baa684c95a512520a42@o4511864217206784.ingest.de.sentry.io/4511870396923984",
			integrations: [new BrowserTracing()],

			// 0.1 = sample 10% of transactions; reduced from 1.0 to save Sentry quota on the EU org
			tracesSampleRate: 0.1,

			// Filter "Non-Error exception" messages from third-party scripts (first line of defense)
			// See: docs/bug-analyzes/2026-02-03 Non-Error CustomEvent Exception (Recurring).md
			ignoreErrors: [
				/Non-Error exception captured/,
				/Non-Error promise rejection captured/,
				/webkitExitFullScreen/, // Firefox iOS injects a fullscreen script that crashes on pages without <video>
				/AbortError: The play\(\) request was interrupted/, // benign Chrome media race (see https://goo.gl/LdLk22)
				/Java object is gone/, // Android in-app-browser JS bridge torn down mid-call
			],

			// Errors raised by browser-extension code (e.g. hooked gtag fetch) are not ours
			denyUrls: [
				/^chrome-extension:\/\//,
				/^moz-extension:\/\//,
				/^safari-extension:\/\//,
			],

			// Filter by exception type and hint (second line of defense)
			// hint.originalException can be undefined in some cases (known Sentry bug)
			beforeSend(event, hint) {
				const error = hint.originalException;
				const isEmptySyntheticOnError = error === undefined && event.exception?.values?.some(exc =>
					exc.type === 'Error' &&
					exc.value === 'undefined' &&
					exc.mechanism?.type === 'onerror' &&
					exc.mechanism?.handled === false &&
					exc.mechanism?.synthetic === true
				);

				// An empty window.onerror call has no error or message to diagnose. Sentry 7
				// otherwise turns it into a synthetic "Error: undefined" at the document URL.
				if (isEmptySyntheticOnError) {
					return null;
				}

				// Check hint.originalException if available
				if (error && typeof error === 'object' && !(error instanceof Error)) {
					if ('isTrusted' in error || 'currentTarget' in error || 'target' in error) {
						return null;
					}
				}

				// Check event.exception type (handles undefined hint.originalException)
				if (event.exception?.values?.some(exc =>
					exc.type === 'CustomEvent' ||
					exc.type === 'Event' ||
					exc.type === 'ProgressEvent'
				)) {
					return null;
				}

				// Chrome iOS runs Google Translate inside the page and its recursion surfaces as ours (NEOS-WEBSITE-FRONTEND-X)
				if (isErrorFromGoogleTranslate(event)) {
					return null;
				}

				return scrubEventUrls(event);
			},

			beforeSendTransaction(event) {
				return scrubEventUrls(event);
			},
		});
	});
}

// Safari 11 polyfill for AlpineJS, see also https://github.com/alpinejs/alpine/discussions/1964
if (typeof window.queueMicrotask !== "function") {
    window.queueMicrotask = function (callback) {
        Promise.resolve()
            .then(callback)
            .catch(e => setTimeout(() => { throw e; }));
    };
}

import Alpine from 'alpinejs';
import focus from '@alpinejs/focus';

window.Alpine = Alpine;
Alpine.plugin(focus);

import './Presentation/Atom/IconTooltip/IconTooltip.js';
import './Presentation/Atom/RichText/RichText.js';
import '../../../../CodeQ.Search/Resources/Private/JavaScript/SearchPopup.js';
import "./Presentation/Organism/LettersList/LettersList";
import './Presentation/Molecule/Accordion/Accordion.js';
import './Presentation/Molecule/TopicLinkList/TopicLinkList.js';
import './scripts/scrollable.js';
import Fixed from './scripts/fixed.js';

Alpine.start();

import 'share-api-polyfill';
import 'lazysizes';

// other distribution packages
import '../../../../CodeQ.Likes/Resources/Private/Fusion/Root.js';
import GoogleRecaptcha from "../../../../CodeQ.RecaptchaAuth/Resources/Private/Fusion/Root";
import "../../../../CodeQ.Shortcuts/Resources/Private/Fusion/Integration/Organism/ShortcutCreationForm/ShortcutCreationForm";
import "../../../../CodeQ.CamBuildr/NodeTypes/Mixin/Form/Form";


import ShareButton from "./Presentation/Atom/ShareButton/ShareButton";
import TopicSlider from "./Presentation/Molecule/TopicSlider/TopicSlider";
import StorySlider from "./Presentation/Molecule/StorySlider/StorySlider";
import PersonSlider from "./Presentation/Molecule/PersonSlider/PersonSlider";
import NewsTeaser from "./Presentation/Molecule/NewsTeaser/NewsTeaser";
import CardGrid from "./Presentation/Molecule/CardGrid/CardGrid";
import MultiSurvey from "./Presentation/Molecule/MultiSurvey/MultiSurvey.js";
import YoutubePlayer from "./Presentation/Molecule/YouTubePlayer/YouTubePlayer";
import YouTubeGrid from "./Presentation/Molecule/YouTubeGrid/YouTubeGrid";
import SocialIconsListShareButtons from "./Presentation/Molecule/SocialIconsList/SocialIconsList.ShareButtons";
import VideoPlayer from './Presentation/Molecule/VideoPlayer/VideoPlayer';
import EventsMap from "./Presentation/Organism/EventsMap/EventsMap";
import EventCountdown from "./Presentation/Organism/EventCountdown/EventCountdown";
import GoogleMap from "./Presentation/Organism/GoogleMap/GoogleMap";
import CrmFormEmbed from "./Presentation/Molecule/CrmFormEmbed/CrmFormEmbed";
import General from "./javascript/general";
import ImageFallback from "./javascript/imageFallback";
import YoutubeSlider from "./Presentation/Organism/YoutubeSlider/YoutubeSlider";
import NotificationBar from "./Presentation/Organism/NotificationBar/NotificationBar";

// Alpine definitions
import "./Presentation/Molecule/ImageSlider/ImageSlider";

class App {
    constructor() {
		General.init();
		ImageFallback.init();
		GoogleRecaptcha.init();

        ShareButton.init();
		TopicSlider.init();
        StorySlider.init();
        PersonSlider.init();
        NewsTeaser.init();
        CardGrid.init();
        MultiSurvey.init();
        YoutubePlayer.init();
        YouTubeGrid.init();
        YoutubeSlider.init();
        SocialIconsListShareButtons.init();
        VideoPlayer.init();
		GoogleMap.init();
        CrmFormEmbed.init();
        Fixed.init();
        EventCountdown.init();
        EventsMap.init();
		NotificationBar.init()
    }

    get() {
        return this;
    }
}

window.app = new App();
